import localforage from 'localforage';
import _ from 'lodash';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataSynchronisationStatus } from './offline-data-fetcher';

export interface Cachable {
    fromUpdateCache?: boolean
}

export interface DataUpdateCacherStats {
    date: number,
    status: DataSynchronisationStatus
}

const offlineDataUpdateCache = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-pending-update-data-cache',
    storeName: 'runtime_cache',
    version: 2.0,
});
const LAST_PUSH_DATE_CACHE_KEY = 'last-push-date';
export class OfflineDataUpdateCacher<T extends any> {

    // methods to manage push stats
    public static async setLastPushStats(date: number, status: DataSynchronisationStatus) {
        await offlineDataUpdateCache.setItem(LAST_PUSH_DATE_CACHE_KEY, { date, status })
    }

    public static async getLastPushStats(): Promise<DataUpdateCacherStats> {
        return await offlineDataUpdateCache.getItem(LAST_PUSH_DATE_CACHE_KEY)
    }

    public static async isPushError(): Promise<boolean> {
        const stats = await this.getLastPushStats();
        if (stats) {
            return stats.status === DataSynchronisationStatus.ERROR ||
                stats.status === DataSynchronisationStatus.ABORTED
        }
        return false;
    }

    private cacheKeyPrefix: string = "";

    constructor(cacheKey: string) {
        this.cacheKeyPrefix = cacheKey;
    }

    public storeBeforePushing(id: string, body: Partial<T>) {
        if (id) {
            Object.defineProperty(body, "fromUpdateCache", {
                enumerable: true,
                value: true,
            })
            offlineDataUpdateCache.setItem(this.getCacheKey(id), body);
        }
    }

    public getCacheKey(id: string): string {
        return `${this.cacheKeyPrefix}-${id}`;
    }

    public getSanitizedId(id: string) {
        return id.substring(id.lastIndexOf('-') + 1);
    }

    public getCachedValue(id: string): Promise<T> {
        const cacheKey = this.getCacheKey(id);
        return offlineDataUpdateCache.getItem(cacheKey)
    }

    public removeCachedValue(id: string) {
        const cacheKey = this.getCacheKey(id);
        return offlineDataUpdateCache.removeItem(cacheKey)
    }

    public async getCachedObjectsKeys(countLocallyCreated: boolean = true): Promise<string[]> {
        const keys = await offlineDataUpdateCache.keys();
        return keys.filter(
            key => {
                const matchPrefix = key.match(`${this.cacheKeyPrefix}-*`)
                const matchNew = !key.includes('_')

                return countLocallyCreated ? matchPrefix : matchNew && matchPrefix;
            }
        );
    }
    /**
     * Returns all objects that were locally created in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyCreatedCachedObjects(): Promise<T[]> {
        return this.getObjectsWithPredicate(id => id.includes('_'))
    }

    /**
     * Returns all objects that were locally modified in offline mode, 
     * and not pushed to the db yet
     */
    public async getLocallyUpdatedCachedObjects() {
        return this.getObjectsWithPredicate(id => !id.includes('_'))
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true) {
        return (await this.getCachedObjectsKeys(countLocallyCreated)).length;
    }

    public async fetchOneFromId(idToMatch: string) {
        const matching = await this.getObjectsWithPredicate(id => id === idToMatch)
        return matching && matching[0] ? matching[0] : null;
    }

    /**
     * Returns the objects in cache matching a predicate
     * @param predicate 
     */
    private async getObjectsWithPredicate(predicate: (id: string) => boolean) {
        return from(this.getCachedObjectsKeys()).pipe(
            map(keys => keys.filter(predicate)),
            map(
                filteredKeys => {
                    return Promise.all(filteredKeys.map(
                        async key => {
                            const entity = await offlineDataUpdateCache.getItem<T>(key)
                            return entity;
                        }
                    ));
                }
            )
        ).toPromise();
    }

}